import React from 'react';
import { Container, Notification, NotificationLight, Tab, TabMenu } from '../styled/TrainerStyled';
import { errorCodes } from '../../../utils/utils';
import { canShowAssistantPanel, canShowCallEventNotification, canShowNotification, isTabActiveOrSinglePanelMode } from '../viewState';
import Languages from '../../common/icons/Languages';

const ACTIVE_COLOR = '#5d5ff9';
const INACTIVE_COLOR = '#333';

function TabIcon({ children, label }: { children: React.ReactNode; label: string }) {
    return (
        <span title={label} aria-label={label} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {children}
        </span>
    );
}

interface TrainerTabsProps {
    tabsEnabled: boolean;
    selectedTab: string;
    tabAssistant: string;
    tabSales: string;
    tabEvaluations: string;
    tabTranslator: string;
    isTrainerEnabled: boolean;
    isSalesEnabled: boolean;
    isEvaluationsEnabled: boolean;
    isTranslatorEnabled: boolean;
    localization: { getText: (key: string) => string };
    isNotificationShown: boolean;
    callEventNotification?: string | null;
    tabActions: {
        onTabAssistant: () => void;
        onTabSales: () => void;
        onTabEvaluations: () => void;
        onTabTranslator: () => void;
    };
    assistantPanel: React.ReactNode;
    salesPanel: React.ReactNode;
    evaluationsPanel: React.ReactNode;
    translatorPanel: React.ReactNode;
}

export function TrainerTabs({
    tabsEnabled,
    selectedTab,
    tabAssistant,
    tabSales,
    tabEvaluations,
    tabTranslator,
    isTrainerEnabled,
    isSalesEnabled,
    isEvaluationsEnabled,
    isTranslatorEnabled,
    localization,
    isNotificationShown,
    callEventNotification,
    tabActions,
    assistantPanel,
    salesPanel,
    evaluationsPanel,
    translatorPanel
}: TrainerTabsProps) {
    return (
        <Container data-testid="trainer-main-panel">
            {tabsEnabled && (
                <TabMenu data-testid="trainer-tab-menu">
                    <Tab
                        data-testid="trainer-tab-assistant"
                        $active={selectedTab === tabAssistant}
                        $disabled={!isTrainerEnabled}
                        onClick={tabActions.onTabAssistant}>
                        {localization.getText('tabs.assistance')}
                    </Tab>
                    {isSalesEnabled && (
                        <Tab
                            data-testid="trainer-tab-sales"
                            $active={selectedTab === tabSales}
                            $disabled={!isSalesEnabled}
                            onClick={tabActions.onTabSales}>
                            {localization.getText('tabs.sales')}
                        </Tab>
                    )}
                    {isEvaluationsEnabled && (
                        <Tab
                            data-testid="trainer-tab-evaluations"
                            $active={selectedTab === tabEvaluations}
                            $disabled={!isEvaluationsEnabled}
                            onClick={tabActions.onTabEvaluations}>
                            {localization.getText('tabs.qa')}
                        </Tab>
                    )}
                    {isTranslatorEnabled && (
                        <Tab
                            data-testid="trainer-tab-translator"
                            $active={selectedTab === tabTranslator}
                            $disabled={!isTranslatorEnabled}
                            onClick={tabActions.onTabTranslator}
                            title={localization.getText('tabs.translator')}>
                            <TabIcon label={localization.getText('tabs.translator')}>
                                <Languages size={16} color={selectedTab === tabTranslator ? ACTIVE_COLOR : INACTIVE_COLOR} />
                            </TabIcon>
                        </Tab>
                    )}
                </TabMenu>
            )}

            {canShowAssistantPanel({ isTrainerEnabled, selectedTab, tabAssistant, tabsEnabled }) && (
                <>
                    {canShowNotification(isNotificationShown) ? (
                        <Notification data-testid="trainer-notification-network">
                            {localization.getText(`warnings.${errorCodes.background.SLOW_NETWORK_WARN}`)}
                        </Notification>
                    ) : null}
                    {canShowCallEventNotification(callEventNotification) ? (
                        <NotificationLight data-testid="trainer-notification-call-event">
                            {localization.getText(`trainer.callEventNotification.${callEventNotification}`)}
                        </NotificationLight>
                    ) : null}
                    <div data-testid="trainer-assistant-panel">{assistantPanel}</div>
                </>
            )}

            {isSalesEnabled && isTabActiveOrSinglePanelMode(selectedTab, tabSales, tabsEnabled) && (
                <div data-testid="trainer-sales-panel">{salesPanel}</div>
            )}
            {isEvaluationsEnabled && isTabActiveOrSinglePanelMode(selectedTab, tabEvaluations, tabsEnabled) && (
                <div data-testid="trainer-evaluations-panel">{evaluationsPanel}</div>
            )}
            {isTranslatorEnabled && isTabActiveOrSinglePanelMode(selectedTab, tabTranslator, tabsEnabled) && (
                <div data-testid="trainer-translator-panel">{translatorPanel}</div>
            )}
        </Container>
    );
}
